import React from 'react';

import ticketIcon from '../../../assets/icons/orderSummary/ticketIcon.svg';
import { appConfig } from '../../../config/app-config';
import { formatPrice } from '../../../shared/utils/format';
import { groupItemsByDelegateRate } from '../../cart/utils/cartGrouping';

const LineItemsPreview = ({ lineItems, showTaxBreakdown }) => {
    const locale = appConfig.defaults.locale;

    // Group items for display
    const groupedItems = React.useMemo(() => groupItemsByDelegateRate(lineItems), [lineItems]);

    const renderTaxBreakdown = (item, qty) => {
        if (!showTaxBreakdown) return null;

        // Try to access priceData from the item (structure from user's JSON)
        // If not directly available on item, it might be in custom properties or we might need to fallback
        // The user's JSON implies 'lineItems' has 'priceData'. groupLineItemsByAssociation spreads ...item, so it should be there.
        const priceData = item.priceData;

        let subtotalVal = 0;
        let taxVal = 0;
        const currencyCode = item.price?.value?.currencyCode || 'USD';

        if (priceData) {
            subtotalVal = (priceData.taxableBasis || priceData.unitAmount - (priceData.unitTaxAmount || 0));
            taxVal = priceData.unitTaxAmount || 0;
        } else {
            return null;
        }

        // Calculations for total line (unit * qty)
        // priceData values are MAJOR units (not cents), so multiply by 100 to get cents for formatPrice
        const lineSubtotal = { centAmount: Math.round(subtotalVal * 100 * qty), currencyCode };
        const lineTax = { centAmount: Math.round(taxVal * 100 * qty), currencyCode };

        return (
            <div className="flex flex-col gap-[2px] text-[12px] font-normal text-[#667085] italic">
                <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{formatPrice(lineSubtotal, locale)}</span>
                </div>
                <div className="flex justify-between">
                    <span>Total tax</span>
                    <span>{formatPrice(lineTax, locale)}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-[2px]">
            {groupedItems.map((group) => (
                <div key={group.key} className="flex flex-col mb-3 last:mb-0">
                    {/* Group Header - Only for named groups (not 'Other') */}
                    {group.key !== 'Other' && (
                        <div className="font-sans font-normal text-[12px] leading-[18px] text-[#667085] mt-1 mb-[8px]">
                            Attendee Type: {group.key}
                        </div>
                    )}

                    {group.items.map((item, idx) => {
                        const name = (item.name && (item.name[locale] || item.name['en'] || Object.values(item.name)[0])) || 'Item';

                        // Use original unit price * qty to show undiscounted price
                        const originalUnitPrice = item.price?.value;
                        const qty = item.quantity || 1;

                        const totalPrice = originalUnitPrice
                            ? {
                                ...originalUnitPrice,
                                centAmount: (originalUnitPrice.centAmount || 0)
                            }
                            : item.totalPrice;

                        // Add-ons are now pre-attached by the grouping utility
                        const addons = item.addons || [];

                        const metaStr = item.venue || '';

                        return (
                            <div key={item.id || idx} className="flex flex-col mt-[2px] pb-[20px] border-b border-[#EAECF0] last:border-b-0 last:pb-0">
                                <div className="flex items-center gap-[2px] text-[#667085] text-sm">
                                    <img src={ticketIcon} alt="" className="w-4 h-4" />
                                    <span>x {qty}</span>
                                </div>
                                <div className="flex justify-between items-start mb-1">
                                    <div className="flex flex-col">
                                        <span className="font-medium text-[#182230] text-sm">{name}</span>
                                        {metaStr && <div className="text-gray-500 text-sm">{metaStr}</div>}
                                    </div>
                                    <div className="font-semibold text-[#182230] text-sm text-right">
                                        {totalPrice ? formatPrice(totalPrice, locale) : '-'}
                                    </div>
                                </div>

                                {/* Tax Breakdown for Main Item */}
                                {renderTaxBreakdown(item, qty)}

                                {/* Display add-ons with prices (deduplicated by name) */}
                                {addons.length > 0 && (
                                    <div className="flex flex-col gap-[2px] mt-[2px]">
                                        {[...new Map(addons.map(a => [a.name, a])).values()].map((addon, addonIdx) => {
                                            const addonMoney = addon.price || addon.total;
                                            const multiplied = {
                                                ...addonMoney,
                                                centAmount: (addonMoney?.centAmount || 0) * qty
                                            };
                                            return (
                                                <div key={addonIdx} className={`flex flex-col gap-[1px] ${showTaxBreakdown ? 'border-t border-[#EAECF0] pt-3' : ''}`}>
                                                    <div className="flex justify-between text-xs font-normal text-[#667085]">
                                                        <span>+ {addon.name}</span>
                                                        <span>{formatPrice(multiplied, locale)}</span>
                                                    </div>
                                                    {/* Tax Breakdown for Add-on */}
                                                    {renderTaxBreakdown(addon, qty)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default LineItemsPreview;
